// apps/vendor/src/lib/i18n/config.ts
//
// Phase 7 Wave 18 Task 76 (Amendment #1) — i18next bootstrap for the vendor
// SPA. Mirrors the `apps/merchant-v3/src/lib/i18n/config.ts` pattern from
// Phase 6/221 so both portals share the same locale lifecycle:
//
//   - Detection chain: cookie → localStorage → navigator → htmlTag
//     (caches: localStorage + cookie; cookie name distinct per portal so
//     vendor and merchant don't stomp each other on shared domains).
//   - Default locale: 'vi' (Printway is VN-first).
//   - Fallback chain: vi → en, fr → en (FR translations may lag EN/VI).
//   - Resources: ship the @printway/shared `common`, `errors`, `cookie`
//     namespaces eagerly so first paint never blocks on a fetch. Per-feature
//     namespaces (orders, products, inventory, wallet, support, onboarding,
//     settings, analytics) land in Task 77 — they lazy-load via
//     `i18next.loadNamespaces()` when a feature component calls
//     `useTranslation('orders')`.
//
// vg-binding: PLAN:task-76
// vg-binding: P7.D-134
// vg-binding: P7.D-152
// vg-binding: AMENDMENT-1

import i18next, { type i18n as I18n } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import { sharedResources } from '@printway/shared/i18n';

// Phase 7 Wave 18 Task 77 (Amendment #1) — eager-load 9 vendor namespaces ×
// 3 locales = 27 JSON bundles. `common` + `settings` ship populated; the
// remaining 7 (orders, products, inventory, wallet, support, onboarding,
// analytics) seed as `{}` and Task 78 batches fill per-feature. Bundling
// eagerly avoids round-trips on first paint and lets Task 78 reference
// `t('orders:list.title')` etc. without lazy-load wiring.
//
// vg-binding: PLAN:task-77
// vg-binding: P7.D-153
// vg-binding: AMENDMENT-1
import viCommon from './locales/vi/common.json';
import viOrders from './locales/vi/orders.json';
import viProducts from './locales/vi/products.json';
import viInventory from './locales/vi/inventory.json';
import viWallet from './locales/vi/wallet.json';
import viSupport from './locales/vi/support.json';
import viOnboarding from './locales/vi/onboarding.json';
import viSettings from './locales/vi/settings.json';
import viAnalytics from './locales/vi/analytics.json';
import viDashboard from './locales/vi/dashboard.json';
import viAuth from './locales/vi/auth.json';
import viNotifChannels from './locales/vi/notifChannels.json';
// vg-binding: P18.D-21 — namespace `integration` (vendor Integration Settings
// page), registered eagerly in all three locales. P18.D-21 requires "i18n
// namespace `integration` đủ 3 locale"; a lazy namespace would render raw keys
// on first paint of a deep-linked `/settings/integration`.
// vg-binding: P18.D-22 — the same catalog carries the empty-state / inline
// handshake-error / show-once copy D-22 mandates, so it must load with the page.
import viIntegration from './locales/vi/integration.json';
import enCommon from './locales/en/common.json';
import enOrders from './locales/en/orders.json';
import enProducts from './locales/en/products.json';
import enInventory from './locales/en/inventory.json';
import enWallet from './locales/en/wallet.json';
import enSupport from './locales/en/support.json';
import enOnboarding from './locales/en/onboarding.json';
import enSettings from './locales/en/settings.json';
import enAnalytics from './locales/en/analytics.json';
import enDashboard from './locales/en/dashboard.json';
import enAuth from './locales/en/auth.json';
import enNotifChannels from './locales/en/notifChannels.json';
import enIntegration from './locales/en/integration.json';
import frCommon from './locales/fr/common.json';
import frOrders from './locales/fr/orders.json';
import frProducts from './locales/fr/products.json';
import frInventory from './locales/fr/inventory.json';
import frWallet from './locales/fr/wallet.json';
import frSupport from './locales/fr/support.json';
import frOnboarding from './locales/fr/onboarding.json';
import frSettings from './locales/fr/settings.json';
import frAnalytics from './locales/fr/analytics.json';
import frDashboard from './locales/fr/dashboard.json';
import frAuth from './locales/fr/auth.json';
import frNotifChannels from './locales/fr/notifChannels.json';
import frIntegration from './locales/fr/integration.json';

/** Canonical lower-case ISO codes used internally by i18next. */
export type Locale = 'vi' | 'en' | 'fr';

/** Display labels for the TopBar dropdown (mapped to user.language `EN/VI/FR`). */
export const LOCALES: ReadonlyArray<{
  code: Locale;
  user: 'EN' | 'VI' | 'FR';
  label: string;
  flag: string;
}> = [
  { code: 'vi', user: 'VI', label: 'Tiếng Việt', flag: 'VI' },
  { code: 'en', user: 'EN', label: 'English', flag: 'EN' },
  { code: 'fr', user: 'FR', label: 'Français', flag: 'FR' },
];

/** All namespaces planned for the vendor portal. `common` + `errors` +
 *  `cookie` ship eagerly via `@printway/shared/i18n.sharedResources`. The
 *  remaining vendor-specific namespaces lazy-load — Task 77 lands their
 *  JSON bundles under `./locales/<lng>/<ns>.json`. */
export const NAMESPACES = [
  'common',
  'errors',
  'cookie',
  'orders',
  'products',
  'inventory',
  'wallet',
  'support',
  'onboarding',
  'settings',
  'analytics',
  'dashboard',
  'auth',
  'notifChannels',
  // vg-binding: P18.D-21 — vendor Integration Settings page (task-33).
  'integration',
] as const;

const VITE_DEFAULT_LANG: Locale = (() => {
  // Vite-style env access without importing types — safe at module load.
  const env =
    typeof globalThis !== 'undefined'
      ? (globalThis as { import?: { meta?: { env?: Record<string, string> } } })
          .import?.meta?.env
      : undefined;
  const fromEnv = env?.VITE_DEFAULT_LANG;
  if (fromEnv === 'en' || fromEnv === 'vi' || fromEnv === 'fr') return fromEnv;
  return 'vi';
})();

/** Map user.language ('EN'|'VI'|'FR') ↔ i18next locale ('en'|'vi'|'fr'). */
export function userLangToLocale(
  lang: 'EN' | 'VI' | 'FR' | string | null | undefined,
): Locale {
  if (lang === 'EN' || lang === 'en') return 'en';
  if (lang === 'FR' || lang === 'fr') return 'fr';
  return 'vi';
}

export function localeToUserLang(loc: Locale): 'EN' | 'VI' | 'FR' {
  if (loc === 'en') return 'EN';
  if (loc === 'fr') return 'FR';
  return 'VI';
}

/** Initialise i18next once; subsequent calls return the same instance. */
let inited = false;
export function initI18n(): I18n {
  if (inited) return i18next;
  inited = true;

  void i18next
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      // Shared resources (errors/cookie + shared common) eager-load so
      // first paint never blocks. Task 77 (Amendment #1) merges the
      // vendor-portal-specific namespaces on top — `common` and `settings`
      // ship populated; the remaining 7 are seeded as `{}` and Task 78
      // batches populate them per-feature.
      //
      // Merge order: spread sharedResources.common first, then vendor
      // common on top — keys defined locally win, sharedResources keys
      // fall through. This preserves any cross-portal `common` keys
      // (e.g. error envelope copy) while letting the vendor portal
      // override portal-specific labels.
      resources: {
        vi: {
          ...sharedResources.vi,
          common: { ...sharedResources.vi.common, ...viCommon },
          orders: viOrders,
          products: viProducts,
          inventory: viInventory,
          wallet: viWallet,
          support: viSupport,
          onboarding: viOnboarding,
          settings: viSettings,
          analytics: viAnalytics,
          dashboard: viDashboard,
          auth: viAuth,
          notifChannels: viNotifChannels,
          integration: viIntegration,
        },
        en: {
          ...sharedResources.en,
          common: { ...sharedResources.en.common, ...enCommon },
          orders: enOrders,
          products: enProducts,
          inventory: enInventory,
          wallet: enWallet,
          support: enSupport,
          onboarding: enOnboarding,
          settings: enSettings,
          analytics: enAnalytics,
          dashboard: enDashboard,
          auth: enAuth,
          notifChannels: enNotifChannels,
          integration: enIntegration,
        },
        fr: {
          ...sharedResources.fr,
          common: { ...sharedResources.fr.common, ...frCommon },
          orders: frOrders,
          products: frProducts,
          inventory: frInventory,
          wallet: frWallet,
          support: frSupport,
          onboarding: frOnboarding,
          settings: frSettings,
          analytics: frAnalytics,
          dashboard: frDashboard,
          auth: frAuth,
          notifChannels: frNotifChannels,
          integration: frIntegration,
        },
      },
      // No explicit `lng` — let LanguageDetector run (cookie/localStorage/
      // navigator/htmlTag) so a user's saved language survives F5 reload.
      // Setting `lng` here overrides the detector and was the root cause of
      // language not persisting (TopBar selector flipped i18next + wrote
      // cookie; on reload, `lng: VITE_DEFAULT_LANG` forced VI before the
      // detector could read the cookie).
      //  fallback chain: vi→en, fr→en, default→en.
      fallbackLng: {
        vi: ['en'],
        fr: ['en'],
        default: [VITE_DEFAULT_LANG],
      },
      supportedLngs: ['vi', 'en', 'fr'],
      defaultNS: 'common',
      // Task 77 — eager-bundle all 9 vendor namespaces alongside the
      // shared `errors` + `cookie` ones so `useTranslation('orders')`
      // returns the right string immediately (no async loadNamespaces).
      ns: [
        'common',
        'errors',
        'cookie',
        'orders',
        'products',
        'inventory',
        'wallet',
        'support',
        'onboarding',
        'settings',
        'analytics',
        'dashboard',
        'auth',
        'notifChannels',
        'integration',
      ],
      fallbackNS: ['common'],
      interpolation: {
        escapeValue: false, // React already escapes
      },
      detection: {
        order: ['cookie', 'localStorage', 'navigator', 'htmlTag'],
        caches: ['localStorage', 'cookie'],
        // Distinct keys per portal — vendor cookie must not collide with
        // merchant on a shared parent domain.
        lookupCookie: 'vendor.lang',
        lookupLocalStorage: 'vendor.lang.v1',
      },
      returnNull: false,
      returnEmptyString: false,
    });

  return i18next;
}

export { i18next };
